/**
 * Mock Data for Connections Section (UI Redesign Branch)
 *
 * Two connection types:
 * 1. MCP Servers — Docker-sandboxed servers speaking MCP protocol (3 tiers)
 * 2. API Connections — Direct REST/GraphQL connections via OpenAPI spec
 */

// ── Types ──────────────────────────────────────────────────────────────────

export type McpTier = 'tier1' | 'tier2' | 'tier3';
export type CircuitBreakerState = 'closed' | 'half_open' | 'open';
export type ConnectionStatus = 'online' | 'offline' | 'degraded' | 'testing';
export type AuthType = 'api_key' | 'oauth2' | 'basic' | 'bearer' | 'custom_headers' | 'none';
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
export type EndpointCategory = 'read' | 'write' | 'admin';
export type ToolPermission = 'auto_approve' | 'require_approval' | 'admin_only' | 'read' | 'write';

// ── MCP Server Interfaces ──────────────────────────────────────────────────

/**
 * connector-status-truth (bug #2). The backend's `computeMcpConnectionState`
 * reason union — surfaced on the card so a "Sign in to use" / "Setup required"
 * affordance can be driven by the REAL usability state instead of the legacy
 * green `status` field. `ok` means usable; everything else needs an action.
 */
export type McpNeedsSetupReason =
  | 'ok'
  | 'needs_signin'
  | 'token_expired'
  | 'never_tested'
  | 'degraded'
  | 'revoked'
  | 'inactive';

export interface McpServer {
  id: string;
  /**
   * Flavor source (bug #6). Non-null when this install was created from a
   * published Marketplace entry (`system_mcp_servers.id`); null for a
   * directly-registered External server. Drives the per-card Flavor badge
   * + the All/Marketplace/External flavor filter.
   */
  systemServerId?: string | null;
  displayName: string;
  description: string;
  namespace: string;
  tier: McpTier;
  endpointUrl: string;
  status: ConnectionStatus;
  isHealthy: boolean;
  // ── connector-status-truth (bug #2) — REAL usability, not legacy status ──
  /** 'connected' = usable now; 'needsSetup' = added but not usable; 'unavailable' = revoked/inactive. */
  connectionState?: 'connected' | 'needsSetup' | 'unavailable';
  isConnected?: boolean;
  /** Why the connector isn't usable (drives the card affordance copy). */
  needsSetupReason?: McpNeedsSetupReason;
  /** True when the system server declares it requires auth (OAuth / token). */
  requiresAuth?: boolean;
  /** True when an OAuth token is present (an OAuth install has been signed in). */
  signedIn?: boolean;
  toolCount: number;
  circuitBreaker: {
    state: CircuitBreakerState;
    failures: number;
    threshold: number;
    lastFailure: string | null;
    resetTimeout: number;
  };
  docker: {
    image: string;
    tag: string;
    cpuLimit: string;
    memoryLimit: string;
    sandboxed: boolean;
  };
  metrics: {
    latencyMs: number | null;
    uptimePercent: number | null;
    requestsPerHour: number | null;
    errorRate: number | null;
  };
  credentials: {
    type: 'token' | 'mtls' | 'none';
    configured: boolean;
  };
  tools: McpTool[];
  createdAt: string;
  updatedAt: string;
}

export interface McpTool {
  name: string;
  description: string;
  isWriteOperation?: boolean;
  permissionLevel: ToolPermission;
  callCount?: number | null;
  avgLatencyMs?: number | null;
}

// ── API Connection Interfaces ──────────────────────────────────────────────

export interface ApiConnection {
  id: string;
  displayName: string;
  description: string;
  baseUrl: string;
  specFormat: 'openapi3' | 'openapi2' | 'graphql';
  authType: AuthType;
  status: ConnectionStatus;
  isHealthy: boolean;
  endpointCount: number;
  endpoints: ApiEndpoint[];
  auth: {
    type: AuthType;
    configured: boolean;
    headerName?: string;
    tokenUrl?: string;
    scopes?: string[];
    /**
     * Codex retest #265 — Custom Headers exposes header NAMES (not
     * values) on the detail page so admins can see which headers
     * the connection injects. Empty array when auth type isn't
     * `custom_headers` or no headers are configured.
     */
    headerNames?: string[];
    /**
     * Codex retest #239 (round 5) — true when at least one secret
     * slot is non-empty. Drives the `••••••` indicators alongside
     * the `configured` status.
     */
    hasCredentials?: boolean;
  };
  metrics: {
    latencyMs: number | null;
    uptimePercent: number | null;
    requestsPerHour: number | null;
    errorRate: number | null;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ApiEndpoint {
  id: string;
  method: HttpMethod;
  path: string;
  operationId: string;
  summary: string;
  category: EndpointCategory;
  enabled: boolean;
  callCount: number;
  avgLatencyMs: number;
}

// ── Health & Monitoring ────────────────────────────────────────────────────

export interface LatencyDataPoint {
  timestamp: string;
  value: number;
}

export interface ErrorLogEntry {
  id: string;
  connectionId: string;
  connectionName: string;
  connectionType: 'mcp' | 'api';
  timestamp: string;
  severity: 'warning' | 'error' | 'critical';
  message: string;
  details?: string;
}

// ── Label Maps ─────────────────────────────────────────────────────────────

export const TIER_LABELS: Record<McpTier, { label: string; description: string }> = {
  tier1: { label: 'Tier 1 — IQ Native', description: 'In-process modules with direct RLS-enforced database access' },
  tier2: { label: 'Tier 2 — Connected', description: 'Docker-sandboxed connectors to external enterprise systems' },
  tier3: { label: 'Tier 3 — Forge', description: 'Auto-generated MCP servers from API specifications' },
};

export const AUTH_LABELS: Record<AuthType, string> = {
  api_key: 'API Key',
  oauth2: 'OAuth 2.0',
  basic: 'Basic Auth',
  bearer: 'Bearer Token',
  custom_headers: 'Custom Headers',
  none: 'No Auth',
};

export const METHOD_COLORS: Record<HttpMethod, string> = {
  GET: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  POST: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  PUT: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  PATCH: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
  DELETE: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
};

export const PERMISSION_LABELS: Record<ToolPermission, { label: string; className: string }> = {
  auto_approve: { label: 'Auto', className: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  require_approval: { label: 'Approval', className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  admin_only: { label: 'Admin Only', className: 'bg-red-500/10 text-red-600 dark:text-red-400' },
  read: { label: 'Read', className: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  write: { label: 'Write', className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
};

// ── Mock MCP Servers ───────────────────────────────────────────────────────

export const MOCK_MCP_SERVERS: McpServer[] = [
  // Tier 1 — IQ Native
  {
    id: 'mcp-iq-core',
    displayName: 'ArgusIQ Core',
    description: 'Core operational data: assets, CMMS, spaces, ticketing, documents',
    namespace: 'iq',
    tier: 'tier1',
    endpointUrl: 'in-process://iq-core',
    status: 'online',
    isHealthy: true,
    toolCount: 15,
    circuitBreaker: { state: 'closed', failures: 0, threshold: 5, lastFailure: null, resetTimeout: 60 },
    docker: { image: 'n/a', tag: 'n/a', cpuLimit: 'n/a', memoryLimit: 'n/a', sandboxed: false },
    metrics: { latencyMs: 45, uptimePercent: 99.97, requestsPerHour: 2840, errorRate: 0.001 },
    credentials: { type: 'none', configured: true },
    tools: [
      { name: 'iq.asset.search', description: 'Search assets by tag, name, location, or type', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 8420, avgLatencyMs: 32 },
      { name: 'iq.asset.get', description: 'Get asset details by tag or ID (alias for getById)', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 15200, avgLatencyMs: 22 },
      { name: 'iq.asset.getById', description: 'Get detailed asset information by ID', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 12600, avgLatencyMs: 18 },
      { name: 'iq.asset.getHistory', description: 'Get maintenance and event history for an asset', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 3200, avgLatencyMs: 85 },
      { name: 'iq.asset.getTelemetry', description: 'Get current and historical telemetry data for an asset', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 9800, avgLatencyMs: 45 },
      { name: 'iq.asset.getDocuments', description: 'Get documents associated with an asset', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 4100, avgLatencyMs: 55 },
      { name: 'iq.document.search', description: 'Search document library by keywords, tags, or content', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 7600, avgLatencyMs: 120 },
      { name: 'iq.document.getContent', description: 'Get full content of a document by ID', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 5400, avgLatencyMs: 85 },
      { name: 'iq.document.getVersions', description: 'Get version history of a document', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 1200, avgLatencyMs: 35 },
      { name: 'iq.document.list', description: 'List documents in a collection or folder', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 3800, avgLatencyMs: 65 },
      { name: 'iq.cmms.searchWorkOrders', description: 'Search work orders by status, assignee, asset', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 5100, avgLatencyMs: 42 },
      { name: 'iq.cmms.createWorkOrder', description: 'Create a new work order', isWriteOperation: true, permissionLevel: 'require_approval', callCount: 340, avgLatencyMs: 120 },
      { name: 'iq.cmms.updateWorkOrder', description: 'Update an existing work order', isWriteOperation: true, permissionLevel: 'require_approval', callCount: 280, avgLatencyMs: 95 },
      { name: 'iq.space.getFloorPlan', description: 'Get floor plan layout for a space', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 1800, avgLatencyMs: 55 },
      { name: 'iq.ticket.create', description: 'Create a support ticket', isWriteOperation: true, permissionLevel: 'require_approval', callCount: 120, avgLatencyMs: 78 },
    ],
    createdAt: '2026-01-15T08:00:00Z',
    updatedAt: '2026-04-17T12:30:00Z',
  },
  {
    id: 'mcp-iq-telemetry',
    displayName: 'ArgusIQ Telemetry',
    description: 'Sensor readings, alarm history, and real-time telemetry data',
    namespace: 'iq',
    tier: 'tier1',
    endpointUrl: 'in-process://iq-telemetry',
    status: 'online',
    isHealthy: true,
    toolCount: 5,
    circuitBreaker: { state: 'closed', failures: 0, threshold: 5, lastFailure: null, resetTimeout: 60 },
    docker: { image: 'n/a', tag: 'n/a', cpuLimit: 'n/a', memoryLimit: 'n/a', sandboxed: false },
    metrics: { latencyMs: 120, uptimePercent: 99.85, requestsPerHour: 1560, errorRate: 0.003 },
    credentials: { type: 'none', configured: true },
    tools: [
      { name: 'iq.telemetry.getCurrent', description: 'Get current sensor readings for a device', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 15200, avgLatencyMs: 28 },
      { name: 'iq.telemetry.getHistory', description: 'Get historical telemetry data with time range', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 4800, avgLatencyMs: 180 },
      { name: 'iq.telemetry.getAlarms', description: 'Get active and recent alarms', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 6300, avgLatencyMs: 45 },
      { name: 'iq.telemetry.acknowledgeAlarm', description: 'Acknowledge an active alarm', isWriteOperation: true, permissionLevel: 'require_approval', callCount: 220, avgLatencyMs: 65 },
      { name: 'iq.analytics.getTrend', description: 'Get trend analysis for a telemetry parameter', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 2100, avgLatencyMs: 250 },
    ],
    createdAt: '2026-01-15T08:00:00Z',
    updatedAt: '2026-04-17T12:30:00Z',
  },

  // Tier 2 — Customer Connected
  {
    id: 'mcp-sap',
    displayName: 'SAP S/4HANA',
    description: 'Enterprise resource planning: purchase orders, materials, plant maintenance',
    namespace: 'ext.sap',
    tier: 'tier2',
    endpointUrl: 'https://sap-mcp.internal:8443',
    status: 'online',
    isHealthy: true,
    toolCount: 6,
    circuitBreaker: { state: 'closed', failures: 1, threshold: 5, lastFailure: '2026-04-16T14:22:00Z', resetTimeout: 60 },
    docker: { image: 'argusai/mcp-sap', tag: 'v2.1.3', cpuLimit: '1', memoryLimit: '512MB', sandboxed: true },
    metrics: { latencyMs: 1200, uptimePercent: 98.5, requestsPerHour: 340, errorRate: 0.012 },
    credentials: { type: 'token', configured: true },
    tools: [
      { name: 'ext.sap.searchParts', description: 'Search SAP material master for parts', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 1200, avgLatencyMs: 850 },
      { name: 'ext.sap.getPartDetails', description: 'Get detailed material master record', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 2400, avgLatencyMs: 620 },
      { name: 'ext.sap.checkStock', description: 'Check inventory stock levels at a plant', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 890, avgLatencyMs: 540 },
      { name: 'ext.sap.createPurchaseOrder', description: 'Create a purchase order for parts', isWriteOperation: true, permissionLevel: 'admin_only', callCount: 45, avgLatencyMs: 2200 },
      { name: 'ext.sap.getPMOrders', description: 'Get plant maintenance orders', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 680, avgLatencyMs: 780 },
      { name: 'ext.sap.readEquipment', description: 'Read equipment master record', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 1100, avgLatencyMs: 450 },
    ],
    createdAt: '2026-02-20T10:00:00Z',
    updatedAt: '2026-04-15T16:45:00Z',
  },
  {
    id: 'mcp-servicenow',
    displayName: 'ServiceNow ITSM',
    description: 'IT service management: incidents, changes, CMDB configuration items',
    namespace: 'ext.snow',
    tier: 'tier2',
    endpointUrl: 'https://servicenow-mcp.internal:8444',
    status: 'offline',
    isHealthy: false,
    toolCount: 4,
    circuitBreaker: { state: 'open', failures: 5, threshold: 5, lastFailure: '2026-04-17T10:15:00Z', resetTimeout: 60 },
    docker: { image: 'argusai/mcp-servicenow', tag: 'v1.3.0', cpuLimit: '1', memoryLimit: '512MB', sandboxed: true },
    metrics: { latencyMs: 0, uptimePercent: 87.2, requestsPerHour: 0, errorRate: 1.0 },
    credentials: { type: 'token', configured: true },
    tools: [
      { name: 'ext.snow.searchIncidents', description: 'Search ServiceNow incidents by priority, state, assignee', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 560, avgLatencyMs: 380 },
      { name: 'ext.snow.createIncident', description: 'Create a new ServiceNow incident', isWriteOperation: true, permissionLevel: 'require_approval', callCount: 85, avgLatencyMs: 520 },
      { name: 'ext.snow.getChanges', description: 'Get change requests and their status', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 340, avgLatencyMs: 290 },
      { name: 'ext.snow.getCMDB', description: 'Query CMDB for configuration items', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 420, avgLatencyMs: 350 },
    ],
    createdAt: '2026-03-01T09:00:00Z',
    updatedAt: '2026-04-17T10:15:00Z',
  },

  // Tier 3 — Forge Generated
  {
    id: 'mcp-forge-weather',
    displayName: 'Weather API (Forge)',
    description: 'Auto-generated from OpenWeatherMap OpenAPI spec',
    namespace: 'forge.weather',
    tier: 'tier3',
    endpointUrl: 'http://forge-weather-mcp:9001',
    status: 'online',
    isHealthy: true,
    toolCount: 3,
    circuitBreaker: { state: 'closed', failures: 0, threshold: 5, lastFailure: null, resetTimeout: 60 },
    docker: { image: 'argusai/forge-mcp-weather', tag: 'v1.0.0-auto', cpuLimit: '0.5', memoryLimit: '256MB', sandboxed: true },
    metrics: { latencyMs: 450, uptimePercent: 96.8, requestsPerHour: 120, errorRate: 0.008 },
    credentials: { type: 'token', configured: true },
    tools: [
      { name: 'forge.weather.getCurrent', description: 'Get current weather conditions for a location', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 680, avgLatencyMs: 320 },
      { name: 'forge.weather.getForecast', description: 'Get 5-day weather forecast', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 450, avgLatencyMs: 480 },
      { name: 'forge.weather.getAlerts', description: 'Get weather alerts for a region', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 220, avgLatencyMs: 280 },
    ],
    createdAt: '2026-04-01T14:00:00Z',
    updatedAt: '2026-04-17T08:00:00Z',
  },
  {
    id: 'mcp-forge-inventory',
    displayName: 'Inventory GraphQL (Forge)',
    description: 'Auto-generated from internal inventory management GraphQL schema',
    namespace: 'forge.inventory',
    tier: 'tier3',
    endpointUrl: 'http://forge-inventory-mcp:9002',
    status: 'degraded',
    isHealthy: false,
    toolCount: 4,
    circuitBreaker: { state: 'half_open', failures: 3, threshold: 5, lastFailure: '2026-04-17T09:45:00Z', resetTimeout: 60 },
    docker: { image: 'argusai/forge-mcp-inventory', tag: 'v1.1.0-auto', cpuLimit: '0.5', memoryLimit: '256MB', sandboxed: true },
    metrics: { latencyMs: 890, uptimePercent: 91.2, requestsPerHour: 85, errorRate: 0.045 },
    credentials: { type: 'token', configured: true },
    tools: [
      { name: 'forge.inventory.search', description: 'Search inventory items by SKU, name, or category', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 340, avgLatencyMs: 560 },
      { name: 'forge.inventory.getStock', description: 'Get stock levels at a warehouse', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 520, avgLatencyMs: 420 },
      { name: 'forge.inventory.reserveStock', description: 'Reserve stock for a work order', isWriteOperation: true, permissionLevel: 'require_approval', callCount: 65, avgLatencyMs: 780 },
      { name: 'forge.inventory.getMovements', description: 'Get stock movement history', isWriteOperation: false, permissionLevel: 'auto_approve', callCount: 180, avgLatencyMs: 650 },
    ],
    createdAt: '2026-04-05T11:00:00Z',
    updatedAt: '2026-04-17T09:45:00Z',
  },
];

// ── Mock API Connections ───────────────────────────────────────────────────

export const MOCK_API_CONNECTIONS: ApiConnection[] = [
  {
    id: 'api-erp-rest',
    displayName: 'ERP REST API',
    description: 'Enterprise resource planning system — materials, vendors, cost centers',
    baseUrl: 'https://erp.acme-industrial.com/api/v2',
    specFormat: 'openapi3',
    authType: 'oauth2',
    status: 'online',
    isHealthy: true,
    endpointCount: 9,
    auth: { type: 'oauth2', configured: true, tokenUrl: 'https://erp.acme-industrial.com/oauth/token', scopes: ['read:materials', 'read:vendors', 'write:orders'] },
    metrics: { latencyMs: 320, uptimePercent: 99.2, requestsPerHour: 580, errorRate: 0.005 },
    endpoints: [
      { id: 'ep-1', method: 'GET', path: '/materials', operationId: 'listMaterials', summary: 'List materials with filtering and pagination', category: 'read', enabled: true, callCount: 1200, avgLatencyMs: 180 },
      { id: 'ep-2', method: 'GET', path: '/materials/{id}', operationId: 'getMaterial', summary: 'Get material details by ID', category: 'read', enabled: true, callCount: 2400, avgLatencyMs: 95 },
      { id: 'ep-3', method: 'POST', path: '/materials', operationId: 'createMaterial', summary: 'Create a new material record', category: 'write', enabled: false, callCount: 0, avgLatencyMs: 0 },
      { id: 'ep-4', method: 'GET', path: '/vendors', operationId: 'listVendors', summary: 'List vendors with search', category: 'read', enabled: true, callCount: 680, avgLatencyMs: 150 },
      { id: 'ep-5', method: 'GET', path: '/vendors/{id}', operationId: 'getVendor', summary: 'Get vendor details', category: 'read', enabled: true, callCount: 920, avgLatencyMs: 85 },
      { id: 'ep-6', method: 'POST', path: '/purchase-orders', operationId: 'createPO', summary: 'Create a purchase order', category: 'write', enabled: true, callCount: 120, avgLatencyMs: 450 },
      { id: 'ep-7', method: 'GET', path: '/purchase-orders', operationId: 'listPOs', summary: 'List purchase orders', category: 'read', enabled: true, callCount: 340, avgLatencyMs: 220 },
      { id: 'ep-8', method: 'GET', path: '/cost-centers', operationId: 'listCostCenters', summary: 'List cost centers', category: 'read', enabled: true, callCount: 280, avgLatencyMs: 120 },
      { id: 'ep-9', method: 'PUT', path: '/materials/{id}', operationId: 'updateMaterial', summary: 'Update material record', category: 'write', enabled: false, callCount: 0, avgLatencyMs: 0 },
      { id: 'ep-10', method: 'DELETE', path: '/materials/{id}', operationId: 'deleteMaterial', summary: 'Delete material record', category: 'admin', enabled: false, callCount: 0, avgLatencyMs: 0 },
      { id: 'ep-11', method: 'GET', path: '/inventory/levels', operationId: 'getInventoryLevels', summary: 'Get inventory levels by plant', category: 'read', enabled: true, callCount: 560, avgLatencyMs: 280 },
      { id: 'ep-12', method: 'PATCH', path: '/purchase-orders/{id}', operationId: 'updatePO', summary: 'Update purchase order status', category: 'write', enabled: true, callCount: 85, avgLatencyMs: 380 },
    ],
    createdAt: '2026-02-10T10:00:00Z',
    updatedAt: '2026-04-16T14:00:00Z',
  },
  {
    id: 'api-scada-gateway',
    displayName: 'SCADA Gateway',
    description: 'Supervisory control and data acquisition — real-time process data',
    baseUrl: 'https://scada-gw.plant-floor.local:9443/api',
    specFormat: 'openapi3',
    authType: 'bearer',
    status: 'degraded',
    isHealthy: false,
    endpointCount: 7,
    auth: { type: 'bearer', configured: true },
    metrics: { latencyMs: 85, uptimePercent: 94.5, requestsPerHour: 2200, errorRate: 0.032 },
    endpoints: [
      { id: 'ep-20', method: 'GET', path: '/tags', operationId: 'listTags', summary: 'List all SCADA tags', category: 'read', enabled: true, callCount: 4500, avgLatencyMs: 35 },
      { id: 'ep-21', method: 'GET', path: '/tags/{tagId}/current', operationId: 'getCurrentValue', summary: 'Get current value for a tag', category: 'read', enabled: true, callCount: 18200, avgLatencyMs: 12 },
      { id: 'ep-22', method: 'GET', path: '/tags/{tagId}/history', operationId: 'getTagHistory', summary: 'Get historical values for a tag', category: 'read', enabled: true, callCount: 3400, avgLatencyMs: 180 },
      { id: 'ep-23', method: 'GET', path: '/alarms/active', operationId: 'getActiveAlarms', summary: 'Get active alarms', category: 'read', enabled: true, callCount: 6800, avgLatencyMs: 28 },
      { id: 'ep-24', method: 'POST', path: '/alarms/{id}/acknowledge', operationId: 'ackAlarm', summary: 'Acknowledge an alarm', category: 'write', enabled: true, callCount: 340, avgLatencyMs: 45 },
      { id: 'ep-25', method: 'GET', path: '/process-areas', operationId: 'listProcessAreas', summary: 'List process areas and their status', category: 'read', enabled: true, callCount: 1200, avgLatencyMs: 55 },
      { id: 'ep-26', method: 'POST', path: '/tags/{tagId}/setpoint', operationId: 'writeSetpoint', summary: 'Write a setpoint value to a tag', category: 'write', enabled: false, callCount: 0, avgLatencyMs: 0 },
      { id: 'ep-27', method: 'GET', path: '/trends/{trendId}', operationId: 'getTrend', summary: 'Get pre-configured trend data', category: 'read', enabled: true, callCount: 2100, avgLatencyMs: 120 },
    ],
    createdAt: '2026-03-05T08:00:00Z',
    updatedAt: '2026-04-17T11:20:00Z',
  },
  {
    id: 'api-cmms-graphql',
    displayName: 'CMMS GraphQL',
    description: 'Computerized maintenance management — work orders, preventive maintenance, assets',
    baseUrl: 'https://cmms.acme-industrial.com/graphql',
    specFormat: 'graphql',
    authType: 'basic',
    status: 'online',
    isHealthy: true,
    endpointCount: 13,
    auth: { type: 'basic', configured: true },
    metrics: { latencyMs: 180, uptimePercent: 99.6, requestsPerHour: 920, errorRate: 0.002 },
    endpoints: [
      { id: 'ep-30', method: 'POST', path: '/graphql', operationId: 'queryWorkOrders', summary: 'Query work orders with filters', category: 'read', enabled: true, callCount: 3200, avgLatencyMs: 120 },
      { id: 'ep-31', method: 'POST', path: '/graphql', operationId: 'queryAssets', summary: 'Query asset hierarchy', category: 'read', enabled: true, callCount: 2800, avgLatencyMs: 95 },
      { id: 'ep-32', method: 'POST', path: '/graphql', operationId: 'queryPMSchedules', summary: 'Query preventive maintenance schedules', category: 'read', enabled: true, callCount: 1500, avgLatencyMs: 140 },
      { id: 'ep-33', method: 'POST', path: '/graphql', operationId: 'createWorkOrder', summary: 'Create a new work order', category: 'write', enabled: true, callCount: 450, avgLatencyMs: 280 },
      { id: 'ep-34', method: 'POST', path: '/graphql', operationId: 'updateWorkOrder', summary: 'Update work order status or details', category: 'write', enabled: true, callCount: 680, avgLatencyMs: 190 },
      { id: 'ep-35', method: 'POST', path: '/graphql', operationId: 'querySpares', summary: 'Query spare parts inventory', category: 'read', enabled: true, callCount: 920, avgLatencyMs: 110 },
      { id: 'ep-36', method: 'POST', path: '/graphql', operationId: 'queryFailures', summary: 'Query failure history and codes', category: 'read', enabled: true, callCount: 780, avgLatencyMs: 160 },
      { id: 'ep-37', method: 'POST', path: '/graphql', operationId: 'createPMTask', summary: 'Create preventive maintenance task', category: 'write', enabled: false, callCount: 0, avgLatencyMs: 0 },
      { id: 'ep-38', method: 'POST', path: '/graphql', operationId: 'queryLaborHours', summary: 'Query labor hours and costs', category: 'read', enabled: true, callCount: 340, avgLatencyMs: 200 },
      { id: 'ep-39', method: 'POST', path: '/graphql', operationId: 'queryDowntime', summary: 'Query equipment downtime records', category: 'read', enabled: true, callCount: 560, avgLatencyMs: 175 },
      { id: 'ep-40', method: 'POST', path: '/graphql', operationId: 'closeWorkOrder', summary: 'Close a work order with completion notes', category: 'write', enabled: true, callCount: 320, avgLatencyMs: 220 },
      { id: 'ep-41', method: 'POST', path: '/graphql', operationId: 'queryCertifications', summary: 'Query technician certifications', category: 'read', enabled: true, callCount: 180, avgLatencyMs: 90 },
      { id: 'ep-42', method: 'POST', path: '/graphql', operationId: 'queryConditionMonitoring', summary: 'Query condition monitoring data', category: 'read', enabled: true, callCount: 420, avgLatencyMs: 250 },
      { id: 'ep-43', method: 'POST', path: '/graphql', operationId: 'deleteWorkOrder', summary: 'Delete a draft work order', category: 'admin', enabled: false, callCount: 0, avgLatencyMs: 0 },
      { id: 'ep-44', method: 'POST', path: '/graphql', operationId: 'queryMetrics', summary: 'Query MTBF/MTTR/OEE metrics', category: 'read', enabled: true, callCount: 650, avgLatencyMs: 310 },
    ],
    createdAt: '2026-03-12T09:00:00Z',
    updatedAt: '2026-04-17T07:30:00Z',
  },
  {
    id: 'api-historian',
    displayName: 'OSIsoft PI Web API',
    description: 'Process historian — time-series data, event frames, asset framework',
    baseUrl: 'https://pi-web.plant-floor.local/piwebapi',
    specFormat: 'openapi2',
    authType: 'api_key',
    status: 'online',
    isHealthy: true,
    endpointCount: 6,
    auth: { type: 'api_key', configured: true, headerName: 'X-PI-WebAPI-Key' },
    metrics: { latencyMs: 65, uptimePercent: 99.8, requestsPerHour: 3400, errorRate: 0.001 },
    endpoints: [
      { id: 'ep-50', method: 'GET', path: '/points', operationId: 'searchPoints', summary: 'Search PI points by name or descriptor', category: 'read', enabled: true, callCount: 5600, avgLatencyMs: 28 },
      { id: 'ep-51', method: 'GET', path: '/streams/{webId}/value', operationId: 'getCurrentValue', summary: 'Get current value for a PI point', category: 'read', enabled: true, callCount: 22000, avgLatencyMs: 8 },
      { id: 'ep-52', method: 'GET', path: '/streams/{webId}/recorded', operationId: 'getRecordedData', summary: 'Get recorded values with time range', category: 'read', enabled: true, callCount: 8400, avgLatencyMs: 120 },
      { id: 'ep-53', method: 'GET', path: '/streams/{webId}/interpolated', operationId: 'getInterpolatedData', summary: 'Get interpolated values at fixed intervals', category: 'read', enabled: true, callCount: 4200, avgLatencyMs: 95 },
      { id: 'ep-54', method: 'GET', path: '/assetdatabases', operationId: 'listAFDatabases', summary: 'List Asset Framework databases', category: 'read', enabled: true, callCount: 320, avgLatencyMs: 45 },
      { id: 'ep-55', method: 'GET', path: '/eventframes', operationId: 'searchEventFrames', summary: 'Search event frames by template and time range', category: 'read', enabled: true, callCount: 1800, avgLatencyMs: 180 },
    ],
    createdAt: '2026-01-20T11:00:00Z',
    updatedAt: '2026-04-17T06:00:00Z',
  },
];

// ── Mock Error Log ─────────────────────────────────────────────────────────

export const MOCK_ERROR_LOG: ErrorLogEntry[] = [
  { id: 'err-1', connectionId: 'mcp-servicenow', connectionName: 'ServiceNow ITSM', connectionType: 'mcp', timestamp: '2026-04-17T10:15:00Z', severity: 'critical', message: 'Circuit breaker opened: 5 consecutive failures', details: 'ECONNREFUSED: connection refused to servicenow-mcp.internal:8444' },
  { id: 'err-2', connectionId: 'mcp-forge-inventory', connectionName: 'Inventory GraphQL (Forge)', connectionType: 'mcp', timestamp: '2026-04-17T09:45:00Z', severity: 'error', message: 'Request timeout after 30s on forge.inventory.getStock', details: 'Upstream server did not respond within deadline' },
  { id: 'err-3', connectionId: 'api-scada-gateway', connectionName: 'SCADA Gateway', connectionType: 'api', timestamp: '2026-04-17T11:20:00Z', severity: 'warning', message: 'Elevated error rate: 3.2% (threshold: 2%)', details: 'Intermittent 503 responses from SCADA gateway' },
  { id: 'err-4', connectionId: 'mcp-sap', connectionName: 'SAP S/4HANA', connectionType: 'mcp', timestamp: '2026-04-16T14:22:00Z', severity: 'warning', message: 'Slow response: ext.sap.createPurchaseOrder averaged 3200ms', details: 'SAP backend under heavy load during batch processing window' },
  { id: 'err-5', connectionId: 'mcp-servicenow', connectionName: 'ServiceNow ITSM', connectionType: 'mcp', timestamp: '2026-04-17T10:12:00Z', severity: 'error', message: 'Connection refused on health check', details: 'ServiceNow MCP container is not responding on port 8444' },
  { id: 'err-6', connectionId: 'mcp-forge-inventory', connectionName: 'Inventory GraphQL (Forge)', connectionType: 'mcp', timestamp: '2026-04-17T09:30:00Z', severity: 'warning', message: 'Circuit breaker half-open: testing recovery', details: 'Sending probe request to verify server recovery' },
  { id: 'err-7', connectionId: 'api-scada-gateway', connectionName: 'SCADA Gateway', connectionType: 'api', timestamp: '2026-04-17T10:55:00Z', severity: 'error', message: 'TLS certificate warning: expires in 14 days', details: 'Certificate for scada-gw.plant-floor.local expires 2026-05-01' },
  { id: 'err-8', connectionId: 'mcp-servicenow', connectionName: 'ServiceNow ITSM', connectionType: 'mcp', timestamp: '2026-04-17T10:05:00Z', severity: 'error', message: 'Health check failed (3/5)', details: 'TCP connect timeout after 10s' },
];

// ── Mock Latency Chart Data ────────────────────────────────────────────────

function generateLatencyData(baseLine: number, variance: number): LatencyDataPoint[] {
  const now = new Date('2026-04-17T12:00:00Z');
  return Array.from({ length: 24 }, (_, i) => ({
    timestamp: new Date(now.getTime() - (23 - i) * 3600000).toISOString(),
    value: Math.round(baseLine + (Math.random() - 0.5) * variance),
  }));
}

export const MOCK_LATENCY_DATA: Record<string, LatencyDataPoint[]> = {
  'mcp-iq-core': generateLatencyData(45, 20),
  'mcp-iq-telemetry': generateLatencyData(120, 40),
  'mcp-sap': generateLatencyData(1200, 600),
  'mcp-servicenow': generateLatencyData(380, 150),
  'mcp-forge-weather': generateLatencyData(450, 100),
  'mcp-forge-inventory': generateLatencyData(890, 300),
  'api-erp-rest': generateLatencyData(320, 80),
  'api-scada-gateway': generateLatencyData(85, 40),
  'api-cmms-graphql': generateLatencyData(180, 60),
  'api-historian': generateLatencyData(65, 25),
};

// ── Custom Functions ───────────────────────────────────────────────────────

export type FunctionLanguage = 'typescript' | 'python';
export type FunctionStatus = 'draft' | 'testing' | 'review' | 'active' | 'disabled' | 'error';

export interface CustomFunction {
  id: string;
  name: string;
  displayName: string;
  description: string;
  language: FunctionLanguage;
  status: FunctionStatus;
  code: string;
  /**
   * Tool/exported-handler count. `null` when telemetry is unavailable
   * (Codex retest #244 — function rollups not wired yet) so cards can
   * render `—` honestly instead of fabricating zeros. Mock fixtures
   * keep populating this with real numbers.
   */
  toolCount: number | null;
  version: string;
  author: string;
  docker: {
    image: string;
    cpuLimit: string;
    memoryLimit: string;
    sandboxed: boolean;
  };
  /**
   * Per-function telemetry. Each field is `null` until per-function
   * aggregation lands; cards must render `—` (not `0`) when null.
   * Codex retest #244.
   */
  metrics: {
    callCount: number | null;
    avgLatencyMs: number | null;
    errorRate: number | null;
  };
  lastDeployed: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * True for published SYSTEM functions (platform-admin authored, org_id IS
   * NULL) surfaced READ-ONLY in a tenant's list. `code` is redacted server-
   * side; the UI renders a System badge and disables edit/deploy/delete.
   */
  isSystem?: boolean;
}

export const FUNCTION_STATUS_STYLES: Record<FunctionStatus, string> = {
  draft: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400',
  testing: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  review: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  active: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  disabled: 'bg-zinc-500/10 text-zinc-500 dark:text-zinc-500',
  error: 'bg-red-500/10 text-red-600 dark:text-red-400',
};

export const MOCK_CUSTOM_FUNCTIONS: CustomFunction[] = [
  {
    id: 'fn-valve-monitor',
    name: 'custom.valve_monitor',
    displayName: 'Valve Position Monitor',
    description: 'Reads control valve positions from plant PLC via Modbus TCP and returns current state with historical trend',
    language: 'typescript',
    status: 'active',
    code: `import { z } from 'zod';

export const manifest = {
  name: 'custom.valve_monitor',
  description: 'Read valve position from PLC',
  parameters: z.object({
    valveId: z.string().describe('Valve tag ID (e.g., FV-401)'),
    includeHistory: z.boolean().default(false),
  }),
};

export async function execute({ valveId, includeHistory }: z.infer<typeof manifest.parameters>) {
  const res = await fetch(\`\${process.env.PLC_API}/valves/\${valveId}\`);
  const data = await res.json();

  if (includeHistory) {
    const history = await fetch(\`\${process.env.PLC_API}/valves/\${valveId}/history?range=24h\`);
    data.history = await history.json();
  }

  return data;
}`,
    toolCount: 1,
    version: '1.2.0',
    author: 'Admin',
    docker: { image: 'argusai/fn-valve-monitor:1.2.0', cpuLimit: '0.5', memoryLimit: '256MB', sandboxed: true },
    metrics: { callCount: 1560, avgLatencyMs: 45, errorRate: 0.005 },
    lastDeployed: '2026-04-15T14:00:00Z',
    createdAt: '2026-03-10T09:00:00Z',
    updatedAt: '2026-04-15T14:00:00Z',
  },
  {
    id: 'fn-shift-notify',
    name: 'custom.shift_notification',
    displayName: 'Shift Notification Sender',
    description: 'Sends structured shift handover notifications to Teams/Slack channels via webhook',
    language: 'typescript',
    status: 'active',
    code: `import { z } from 'zod';

export const manifest = {
  name: 'custom.shift_notification',
  description: 'Send shift handover notification',
  parameters: z.object({
    channel: z.enum(['operations', 'maintenance', 'safety']),
    message: z.string(),
    priority: z.enum(['low', 'normal', 'high', 'critical']).default('normal'),
  }),
};

export async function execute({ channel, message, priority }) {
  const webhook = process.env[\`WEBHOOK_\${channel.toUpperCase()}\`];
  const res = await fetch(webhook, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text: message, priority }),
  });
  return { sent: res.ok, channel, timestamp: new Date().toISOString() };
}`,
    toolCount: 1,
    version: '1.0.3',
    author: 'Admin',
    docker: { image: 'argusai/fn-shift-notify:1.0.3', cpuLimit: '0.5', memoryLimit: '256MB', sandboxed: true },
    metrics: { callCount: 420, avgLatencyMs: 320, errorRate: 0.01 },
    lastDeployed: '2026-04-12T08:00:00Z',
    createdAt: '2026-03-20T11:00:00Z',
    updatedAt: '2026-04-12T08:00:00Z',
  },
  {
    id: 'fn-anomaly-detect',
    name: 'custom.anomaly_detector',
    displayName: 'Vibration Anomaly Detector',
    description: 'Python ML model that analyzes vibration sensor data and detects anomalies using isolation forest',
    language: 'python',
    status: 'active',
    code: `"""Vibration anomaly detection using Isolation Forest."""
import numpy as np
from sklearn.ensemble import IsolationForest

class Tool:
    def __init__(self):
        self.name = "custom.anomaly_detector"
        self.description = "Detect vibration anomalies"
        self.model = IsolationForest(contamination=0.05, random_state=42)

    async def run(self, equipment_tag: str, time_range: str = "24h") -> dict:
        # Fetch vibration data from historian
        data = await self._fetch_vibration_data(equipment_tag, time_range)

        # Detect anomalies
        values = np.array(data["values"]).reshape(-1, 1)
        predictions = self.model.fit_predict(values)
        anomalies = [
            {"timestamp": data["timestamps"][i], "value": data["values"][i]}
            for i, pred in enumerate(predictions) if pred == -1
        ]

        return {
            "equipment": equipment_tag,
            "anomaly_count": len(anomalies),
            "anomalies": anomalies,
            "severity": "high" if len(anomalies) > 5 else "medium" if len(anomalies) > 2 else "low"
        }`,
    toolCount: 1,
    version: '2.1.0',
    author: 'Data Science Team',
    docker: { image: 'argusai/fn-anomaly-detect:2.1.0', cpuLimit: '1', memoryLimit: '512MB', sandboxed: true },
    metrics: { callCount: 890, avgLatencyMs: 1200, errorRate: 0.008 },
    lastDeployed: '2026-04-10T16:00:00Z',
    createdAt: '2026-02-15T10:00:00Z',
    updatedAt: '2026-04-10T16:00:00Z',
  },
  {
    id: 'fn-report-gen',
    name: 'custom.report_generator',
    displayName: 'PDF Report Generator',
    description: 'Generates formatted PDF reports from structured data using WeasyPrint',
    language: 'python',
    status: 'testing',
    code: `"""PDF report generation tool."""
from weasyprint import HTML
import jinja2

class Tool:
    def __init__(self):
        self.name = "custom.report_generator"
        self.description = "Generate PDF report"

    async def run(self, template: str, data: dict, title: str = "Report") -> dict:
        env = jinja2.Environment()
        tmpl = env.from_string(template)
        html_content = tmpl.render(title=title, **data)

        pdf = HTML(string=html_content).write_pdf()
        # Store PDF and return URL
        return {"url": f"/reports/{title}.pdf", "size_bytes": len(pdf)}`,
    toolCount: 1,
    version: '0.3.0',
    author: 'Admin',
    docker: { image: 'argusai/fn-report-gen:0.3.0', cpuLimit: '1', memoryLimit: '512MB', sandboxed: true },
    metrics: { callCount: 34, avgLatencyMs: 4500, errorRate: 0.06 },
    lastDeployed: null,
    createdAt: '2026-04-05T14:00:00Z',
    updatedAt: '2026-04-17T11:00:00Z',
  },
  {
    id: 'fn-data-transform',
    name: 'custom.csv_transformer',
    displayName: 'CSV Data Transformer',
    description: 'Parses, filters, and transforms CSV data for analysis — supports aggregation, pivoting, and column mapping',
    language: 'python',
    status: 'draft',
    code: `"""CSV data transformation tool."""
import pandas as pd
import io

class Tool:
    def __init__(self):
        self.name = "custom.csv_transformer"
        self.description = "Transform CSV data"

    async def run(self, csv_data: str, operations: list) -> dict:
        df = pd.read_csv(io.StringIO(csv_data))
        # Apply operations...
        return {"rows": len(df), "columns": list(df.columns)}`,
    toolCount: 1,
    version: '0.1.0',
    author: 'Data Science Team',
    docker: { image: 'argusai/fn-csv-transform:0.1.0', cpuLimit: '0.5', memoryLimit: '256MB', sandboxed: true },
    metrics: { callCount: 0, avgLatencyMs: 0, errorRate: 0 },
    lastDeployed: null,
    createdAt: '2026-04-16T10:00:00Z',
    updatedAt: '2026-04-17T15:00:00Z',
  },
];

// ── MCP Marketplace ────────────────────────────────────────────────────────

export type MarketplaceCategory = 'data' | 'devops' | 'productivity' | 'ai-ml' | 'monitoring' | 'communication' | 'storage' | 'security';
export type TrustLevel = 'verified' | 'community' | 'official';

export interface MarketplaceServer {
  id: string;
  name: string;
  description: string;
  author: string;
  category: MarketplaceCategory;
  trustLevel: TrustLevel;
  toolCount: number;
  tools: string[];
  installCount: number;
  rating: number;
  reviewCount: number;
  version: string;
  license: string;
  tags: string[];
  dockerImage: string;
  requiresAuth: boolean;
  /** Raw scheme. Optional for back-compat with mock fixtures. */
  authType?: string;
  /** User-facing label for the install dialog. Codex retest #160. */
  authTypeLabel?: string;
  lastUpdated: string;
  installed: boolean;
}

export const MARKETPLACE_CATEGORIES: Record<MarketplaceCategory, { label: string; color: string }> = {
  data: { label: 'Data & Databases', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  devops: { label: 'DevOps & CI/CD', color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400' },
  productivity: { label: 'Productivity', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  'ai-ml': { label: 'AI & ML', color: 'bg-violet-500/10 text-violet-600 dark:text-violet-400' },
  monitoring: { label: 'Monitoring', color: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400' },
  communication: { label: 'Communication', color: 'bg-pink-500/10 text-pink-600 dark:text-pink-400' },
  storage: { label: 'Storage & Files', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  security: { label: 'Security', color: 'bg-red-500/10 text-red-600 dark:text-red-400' },
};

export const TRUST_LEVEL_STYLES: Record<TrustLevel, string> = {
  official: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  verified: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  community: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20',
};

export const MOCK_MARKETPLACE: MarketplaceServer[] = [
  {
    id: 'mp-postgres',
    name: 'PostgreSQL MCP Server',
    description: 'Query PostgreSQL databases with schema inspection, query execution, table management, and connection pooling. Supports read-only and read-write modes.',
    author: 'Anthropic',
    category: 'data',
    trustLevel: 'official',
    toolCount: 6,
    tools: ['query_database', 'list_tables', 'get_schema', 'create_table', 'insert_data', 'estimate_query_cost'],
    installCount: 12400,
    rating: 4.8,
    reviewCount: 342,
    version: '2.1.0',
    license: 'MIT',
    tags: ['sql', 'database', 'postgresql', 'analytics'],
    dockerImage: 'mcp/postgres-server:2.1.0',
    requiresAuth: true,
    authType: 'Connection string',
    lastUpdated: '2026-04-10T00:00:00Z',
    installed: false,
  },
  {
    id: 'mp-github',
    name: 'GitHub MCP Server',
    description: 'Full GitHub API access: repositories, issues, pull requests, code search, actions, releases, and organization management.',
    author: 'GitHub',
    category: 'devops',
    trustLevel: 'official',
    toolCount: 18,
    tools: ['search_repos', 'get_repo', 'list_issues', 'create_issue', 'get_pr', 'merge_pr', 'search_code', 'get_file', 'list_actions', 'trigger_workflow', 'list_releases', 'create_release', 'get_org', 'list_members', 'get_commit', 'compare_commits', 'list_branches', 'create_branch'],
    installCount: 8900,
    rating: 4.7,
    reviewCount: 256,
    version: '3.0.1',
    license: 'MIT',
    tags: ['github', 'git', 'ci-cd', 'code', 'devops'],
    dockerImage: 'mcp/github-server:3.0.1',
    requiresAuth: true,
    authType: 'Personal Access Token',
    lastUpdated: '2026-04-15T00:00:00Z',
    installed: false,
  },
  {
    id: 'mp-slack',
    name: 'Slack MCP Server',
    description: 'Send messages, manage channels, search conversations, upload files, and manage users in Slack workspaces.',
    author: 'Smithery',
    category: 'communication',
    trustLevel: 'verified',
    toolCount: 10,
    tools: ['send_message', 'list_channels', 'search_messages', 'upload_file', 'get_user', 'create_channel', 'set_topic', 'add_reaction', 'get_thread', 'list_members'],
    installCount: 6200,
    rating: 4.5,
    reviewCount: 178,
    version: '1.4.2',
    license: 'Apache-2.0',
    tags: ['slack', 'messaging', 'notifications', 'collaboration'],
    dockerImage: 'smithery/mcp-slack:1.4.2',
    requiresAuth: true,
    authType: 'OAuth 2.0 Bot Token',
    lastUpdated: '2026-04-08T00:00:00Z',
    installed: false,
  },
  {
    id: 'mp-jira',
    name: 'Jira MCP Server',
    description: 'Create and manage Jira issues, search with JQL, manage sprints, track time, and generate reports.',
    author: 'Atlassian Community',
    category: 'productivity',
    trustLevel: 'verified',
    toolCount: 12,
    tools: ['search_issues', 'create_issue', 'update_issue', 'get_issue', 'list_projects', 'get_sprint', 'log_time', 'add_comment', 'transition_issue', 'list_boards', 'get_backlog', 'create_sprint'],
    installCount: 4800,
    rating: 4.3,
    reviewCount: 134,
    version: '1.2.0',
    license: 'MIT',
    tags: ['jira', 'project-management', 'agile', 'ticketing'],
    dockerImage: 'community/mcp-jira:1.2.0',
    requiresAuth: true,
    authType: 'API Token + Email',
    lastUpdated: '2026-03-28T00:00:00Z',
    installed: false,
  },
  {
    id: 'mp-prometheus',
    name: 'Prometheus MCP Server',
    description: 'Query Prometheus metrics, execute PromQL, get alert status, and retrieve target health information.',
    author: 'Glama',
    category: 'monitoring',
    trustLevel: 'verified',
    toolCount: 7,
    tools: ['query_instant', 'query_range', 'list_metrics', 'get_alerts', 'get_targets', 'get_rules', 'get_metadata'],
    installCount: 3400,
    rating: 4.6,
    reviewCount: 89,
    version: '1.1.0',
    license: 'Apache-2.0',
    tags: ['prometheus', 'monitoring', 'metrics', 'alerting', 'observability'],
    dockerImage: 'glama/mcp-prometheus:1.1.0',
    requiresAuth: true,
    authType: 'Bearer Token',
    lastUpdated: '2026-04-02T00:00:00Z',
    installed: true,
  },
  {
    id: 'mp-s3',
    name: 'AWS S3 MCP Server',
    description: 'Manage S3 buckets and objects: list, upload, download, copy, and generate pre-signed URLs.',
    author: 'AWS Community',
    category: 'storage',
    trustLevel: 'community',
    toolCount: 8,
    tools: ['list_buckets', 'list_objects', 'get_object', 'put_object', 'delete_object', 'copy_object', 'get_presigned_url', 'get_bucket_info'],
    installCount: 2100,
    rating: 4.1,
    reviewCount: 67,
    version: '1.0.4',
    license: 'MIT',
    tags: ['aws', 's3', 'storage', 'cloud', 'files'],
    dockerImage: 'community/mcp-s3:1.0.4',
    requiresAuth: true,
    authType: 'AWS Access Key + Secret',
    lastUpdated: '2026-03-20T00:00:00Z',
    installed: false,
  },
  {
    id: 'mp-elasticsearch',
    name: 'Elasticsearch MCP Server',
    description: 'Full-text search, index management, document CRUD, aggregations, and cluster health monitoring.',
    author: 'Elastic Community',
    category: 'data',
    trustLevel: 'community',
    toolCount: 9,
    tools: ['search', 'get_document', 'index_document', 'delete_document', 'list_indices', 'get_mapping', 'aggregate', 'cluster_health', 'create_index'],
    installCount: 1800,
    rating: 4.2,
    reviewCount: 45,
    version: '1.0.2',
    license: 'Apache-2.0',
    tags: ['elasticsearch', 'search', 'analytics', 'logging'],
    dockerImage: 'community/mcp-elasticsearch:1.0.2',
    requiresAuth: true,
    authType: 'API Key or Basic Auth',
    lastUpdated: '2026-03-15T00:00:00Z',
    installed: false,
  },
  {
    id: 'mp-vault',
    name: 'HashiCorp Vault MCP Server',
    description: 'Secret management: read/write secrets, manage policies, handle PKI certificates, and rotate credentials.',
    author: 'Security Community',
    category: 'security',
    trustLevel: 'community',
    toolCount: 6,
    tools: ['read_secret', 'write_secret', 'list_secrets', 'get_policy', 'issue_certificate', 'rotate_credentials'],
    installCount: 1200,
    rating: 4.4,
    reviewCount: 38,
    version: '0.9.1',
    license: 'MPL-2.0',
    tags: ['vault', 'secrets', 'security', 'pki', 'credentials'],
    dockerImage: 'community/mcp-vault:0.9.1',
    requiresAuth: true,
    authType: 'Vault Token',
    lastUpdated: '2026-04-05T00:00:00Z',
    installed: false,
  },
  {
    id: 'mp-grafana',
    name: 'Grafana MCP Server',
    description: 'Query dashboards, manage data sources, retrieve panel data, and manage alerts in Grafana.',
    author: 'Grafana Labs Community',
    category: 'monitoring',
    trustLevel: 'verified',
    toolCount: 8,
    tools: ['list_dashboards', 'get_dashboard', 'get_panel_data', 'list_datasources', 'query_datasource', 'list_alerts', 'silence_alert', 'get_annotations'],
    installCount: 2800,
    rating: 4.5,
    reviewCount: 92,
    version: '1.3.0',
    license: 'Apache-2.0',
    tags: ['grafana', 'dashboards', 'monitoring', 'visualization'],
    dockerImage: 'grafana-community/mcp-grafana:1.3.0',
    requiresAuth: true,
    authType: 'API Key',
    lastUpdated: '2026-04-12T00:00:00Z',
    installed: true,
  },
  {
    id: 'mp-huggingface',
    name: 'HuggingFace MCP Server',
    description: 'Search models, run inference, manage datasets, and interact with the HuggingFace Hub API.',
    author: 'AI Community',
    category: 'ai-ml',
    trustLevel: 'community',
    toolCount: 7,
    tools: ['search_models', 'get_model_info', 'run_inference', 'list_datasets', 'get_dataset_info', 'search_spaces', 'get_model_card'],
    installCount: 3200,
    rating: 4.3,
    reviewCount: 76,
    version: '1.1.0',
    license: 'Apache-2.0',
    tags: ['huggingface', 'ml', 'models', 'inference', 'datasets'],
    dockerImage: 'community/mcp-huggingface:1.1.0',
    requiresAuth: true,
    authType: 'HuggingFace Token',
    lastUpdated: '2026-04-01T00:00:00Z',
    installed: false,
  },
];
